import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Alert,
    CircularProgress,
    Chip,
    Autocomplete,
    TextField
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';

import { formatCurrency, formatDate } from '../../utils/format';
import api from '../../services/api';

const RelatorioAtrasados = () => {
    const [clienteId, setClienteId] = useState('');
    const [clientes, setClientes] = useState([]);
    const [relatorio, setRelatorio] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Carregar relatório e extrair clientes
    const gerarRelatorio = async () => {
        setLoading(true);
        setError('');

        try {
            const params = {};

            if (clienteId) {
                params.id_cliente = clienteId;
            }

            const response = await api.get('/relatorios/atrasados', { params });
            setRelatorio(response.data);

            // Se não estiver filtrando por cliente (carregamento inicial ou limpeza),
            // atualizar a lista de clientes com nomes únicos presentes no relatório
            if (!clienteId) {
                const clientesUnicos = [];
                const map = new Map();

                response.data.forEach(item => {
                    if (item.id_cliente && !map.has(item.id_cliente)) {
                        map.set(item.id_cliente, true);
                        clientesUnicos.push({
                            id: item.id_cliente,
                            nome: item.nome_cliente
                        });
                    }
                });

                // Ordenar alfabeticamente
                clientesUnicos.sort((a, b) => a.nome.localeCompare(b.nome));
                setClientes(clientesUnicos);
            }

        } catch (error) {
            console.error('Erro ao gerar relatório:', error);
            setError('Erro ao gerar relatório de atrasados');
        } finally {
            setLoading(false);
        }
    };

    // Carregar relatório automaticamente ao entrar na página
    useEffect(() => {
        gerarRelatorio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calcularTotalAtrasado = () => {
        return relatorio.reduce((total, item) => total + parseFloat(item.valor_atual || 0), 0);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    Relatório de Parcelas em Atraso
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    sx={{ display: { print: 'none' } }}
                >
                    Imprimir Relatório
                </Button>
            </Box>

            <Paper sx={{ p: 3, mb: 3, display: { print: 'none' } }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <Autocomplete
                            options={clientes}
                            getOptionLabel={(option) => option.nome}
                            value={clientes.find(c => c.id === clienteId) || null}
                            onChange={(event, newValue) => {
                                setClienteId(newValue ? newValue.id : '');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filtrar por Cliente"
                                    placeholder="Digite o nome do cliente"
                                />
                            )}
                            noOptionsText="Nenhum cliente encontrado"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <Button
                            variant="contained"
                            onClick={gerarRelatorio}
                            disabled={loading}
                            fullWidth
                            sx={{ height: 56 }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Atualizar Relatório'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Paper id="relatorio-content" sx={{ p: 3 }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Resultados ({relatorio.length} registros)
                    </Typography>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                            Total em Atraso: {formatCurrency(calcularTotalAtrasado())}
                        </Typography>
                    </Box>
                </Box>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Data Vencimento</TableCell>
                                <TableCell>Cliente</TableCell>
                                <TableCell>Telefone</TableCell>
                                <TableCell>Parcela</TableCell>
                                <TableCell align="center">Dias Atraso</TableCell>
                                <TableCell align="right">Valor Original</TableCell>
                                <TableCell align="right">Valor Atual</TableCell>
                                <TableCell align="center">Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {relatorio.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        Nenhuma parcela atrasada encontrada.
                                    </TableCell>
                                </TableRow>
                            )}
                            {relatorio.map((item, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>{formatDate(item.data_vencimento)}</TableCell>
                                    <TableCell>{item.nome_cliente}</TableCell>
                                    <TableCell>{item.telefone_cliente}</TableCell>
                                    <TableCell>{item.numero_parcela}</TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={`${item.dias_atraso} dias`}
                                            color={item.dias_atraso > 30 ? "error" : "warning"}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(item.valor_original)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {formatCurrency(item.valor_atual)}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={item.status}
                                            color={item.status === 'atrasado' ? "error" : "default"}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Botão de Imprimir Flutuante para Mobile */}
            <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handlePrint}
                    sx={{ borderRadius: '50%', minWidth: 56, height: 56 }}
                >
                    <PrintIcon />
                </Button>
            </Box>

            <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #relatorio-content, #relatorio-content * {
            visibility: visible;
          }
          #relatorio-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 0;
            box-shadow: none !important;
          }
          /* Ocultar cabeçalhos e rodapés do navegador se possível */
          @page {
            margin: 1cm;
          }
        }
      `}</style>
        </Box>
    );
};

export default RelatorioAtrasados;
